import React from 'react'
import PropTypes from 'prop-types'

import { Card, CardHeading, CardContent, CardFooter } from '@components/layout/Card'
import { Slider } from '@components/Slider'
import Box from '@material-ui/core/Box'

import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'
import useModalVisibilityControls from '@hooks/useModalVisibilityControls'

import { EditAssumptionsModal } from './EditAssumptionsModal'

export const Assumptions = ({
  updateScenario,
  clientCurrentAge,
  baseScenario,
  comparedScenario
}) => {
  const {
    status,
    planType,
    expectedAge,
    inflation
  } = baseScenario

  if (status === 'draft' && planType === 'scenario') {
    return (
      <ScenarioSliders
        baseScenario={baseScenario}
        clientCurrentAge={clientCurrentAge}
        updateScenario={updateScenario}
        comparedScenario={comparedScenario}
      />
    )
  }
  return (
    <AssumptionsCard
      inflation={inflation}
      expectedAge={expectedAge}
      updateScenario={updateScenario}
      planType={planType}
    />
  )
}

const ScenarioSliders = ({
  updateScenario,
  clientCurrentAge,
  baseScenario,
  comparedScenario
}) => {
  const [retirementAge, setRetirementAge] = React.useState(baseScenario.retirementAge)
  const [expectedAge, setExpectedAge] = React.useState(baseScenario.expectedAge)

  const minRetirementAge = clientCurrentAge
  const maxRetirementAge = baseScenario.maxExpectedAge - 1

  const minExpectedAge = clientCurrentAge
  const maxExpectedAge = baseScenario.maxExpectedAge

  const onChangeRetirementAge = (_e, value) => {
    setRetirementAge(value);
    setExpectedAge(state => state <= value ? value + 1 : state)
  }

  const onChangeExpectedAge = (_e, value) => {
    setExpectedAge(value <= retirementAge ? retirementAge + 1 : value)
  }

  const onChangeCommitted = () => {
    updateScenario({
      retirement_age: retirementAge,
      expected_age: expectedAge
    })
  }

  return (
    <>
      <Box className='app-slider' width='100%'>
        <h5>Retirement age</h5>
        <label style={{ textAlign: 'left' }}>{comparedScenario.retirementAge} years</label>
        <Box display='flex' justifyContent='space-between'>
          <p className="mb-0">{minRetirementAge}</p>
          <p className="mb-0">{maxRetirementAge}</p>
        </Box>
        <Slider
          onChange={onChangeRetirementAge}
          onChangeCommitted={onChangeCommitted}
          value={retirementAge}
          defaultValue={comparedScenario.retirementAge}
          min={minRetirementAge}
          max={maxRetirementAge}
          step={1}
        />
        <Box display='flex' justifyContent='center'>
          <label>{retirementAge}</label>
        </Box>
      </Box>

      <Box className='app-slider' width='100%'>
        <h5>Life Expectancy</h5>
        <label style={{ textAlign: 'left' }}>{comparedScenario.expectedAge} years</label>
        <Box display='flex' justifyContent='space-between'>
          <p className="mb-0">{minExpectedAge
          }</p>
          <p className="mb-0">{maxExpectedAge}</p>
        </Box>
        <Slider
          onChange={onChangeExpectedAge}
          onChangeCommitted={onChangeCommitted}
          value={expectedAge}
          defaultValue={comparedScenario.expectedAge}
          min={minExpectedAge}
          max={maxExpectedAge}
          step={1}
        />
        <Box display='flex' justifyContent='center'>
          <label>{expectedAge}</label>
        </Box>
      </Box>

    </>
  )
}

const AssumptionsCard = ({
  expectedAge,
  inflation,
  updateScenario,
  planType
}) => {
  const [
    { isEnabled: assumptionsViewEnabled },
    { isEnabled: assumptionsEditEnabled }
  ] = useFeatureConfig(
    FeatureNames.ASSUMPTION__VIEW,
    FeatureNames.ASSUMPTION__EDIT
  )
  const {
    modalVisible,
    closeModal,
    openModal
  } = useModalVisibilityControls()

  if (!assumptionsViewEnabled()) {
    return null
  }

  const onUpdateAssumptions = ({ inflation, annual_increase_in_monthly_contribution }) => {
    updateScenario({ annual_increase_in_monthly_contribution, inflation })
    closeModal()
  }

  return (
    <>
      {assumptionsEditEnabled() && planType !== 'historic' &&

        <EditAssumptionsModal
          open={modalVisible}
          onClose={closeModal}
          onUpdateAssumptions={onUpdateAssumptions}
          inflation={inflation}
        />
      }
      <Card className='ui-card--general-assumptions hidden sm:block'>
        <CardHeading>
          <h5>General Assumptions</h5>
        </CardHeading>
        <CardContent>
          <div className='assumption-block'>
            <p>Life Expectancy</p>
            <p className='assumption'>{expectedAge} years</p>
          </div>
          <div className='assumption-block'>
            <p>Inflation rate</p>
            <p className='assumption'>{formatPercentage(inflation)}</p>
          </div>
        </CardContent>
        {assumptionsEditEnabled() && planType !== 'historic' &&
          <CardFooter className='show-for-medium'>
            <a onClick={openModal}>Edit Assumptions</a>
          </CardFooter>
        }
      </Card>
    </>
  )
}

/* In order to avoid 0.07 * 100 = 7.00000000001, we use toFixed but then cast
 * the string back to a number using the + operator.
 * See this for a longer explanation: https://groups.google.com/g/microsoft.public.scripting.jscript/c/0oGRV2KiJwM
 * */
const formatPercentage = (percentage) => `${+(percentage * 100).toFixed(2)}%`

Assumptions.propTypes = {
  updateScenario: PropTypes.func.isRequired,
  clientCurrentAge: PropTypes.number,

  baseScenario: PropTypes.exact({
    status: PropTypes.string.isRequired,
    planType: PropTypes.string.isRequired,
    retirementAge: PropTypes.number.isRequired,
    annualIncreaseInMonthlyContribution: PropTypes.number.isRequired,
    inflation: PropTypes.number.isRequired,
    expectedAge: PropTypes.number.isRequired,
    maxExpectedAge: PropTypes.number.isRequired
  }).isRequired,

  comparedScenario: PropTypes.exact({
    retirementAge: PropTypes.number,
    expectedAge: PropTypes.number,
    annualIncreaseInMonthlyContribution: PropTypes.number,
  })
}
